import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigation } from "@/components/ui/navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { fetchSubjectOutline } from "@/lib/api/subjects";
import { clearAnsweredQuestions, fetchAnsweredQuestionsDetailed } from "@/lib/api/practice";

const DIFFICULTIES = ["All", "Easy", "Medium", "Hard"] as const;

export default function PracticeSetup() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const outlineQuery = useQuery({
    queryKey: ["subject-outline", slug],
    queryFn: () => fetchSubjectOutline(slug!),
    enabled: Boolean(slug),
  });

  const subject = outlineQuery.data?.subject ?? null;
  const units = outlineQuery.data?.units ?? [];

  const unitAliasMap = useMemo(() => {
    const map = new Map<string, string>();
    units.forEach((unit) => {
      map.set(unit.id, unit.id);
      if (unit.slug) {
        map.set(unit.slug, unit.id);
        if (subject?.slug && unit.slug.startsWith(`${subject.slug}-`)) {
          map.set(unit.slug.slice(subject.slug.length + 1), unit.id);
        }
      }
    });
    return map;
  }, [units, subject?.slug]);

  const topicToUnitId = useMemo(() => {
    const map = new Map<string, string>();
    units.forEach((unit) => {
      unit.topics.forEach((topic) => {
        if (topic.slug) {
          map.set(topic.slug, topic.unit_id ?? unit.id);
        }
      });
    });
    return map;
  }, [units]);

  const answeredQuery = useQuery({
    queryKey: ["answered-questions", user?.id, subject?.id],
    queryFn: () => fetchAnsweredQuestionsDetailed(user!.id, subject!.id, 200),
    enabled: Boolean(user?.id && subject?.id),
  });

  const clearAnsweredMutation = useMutation({
    mutationFn: () => clearAnsweredQuestions(user!.id, subject!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["answered-questions", user?.id, subject?.id] });
      toast({ title: "Cleared", description: "Answered questions reset." });
    },
  });

  // Map unit and topic titles for friendly display (no raw IDs)
  const unitTitleMap = useMemo(() => {
    const map = new Map<string, string>();
    units.forEach((unit) => {
      const title = (unit.name ?? (unit as { title?: string }).title ?? unit.id) as string;
      map.set(unit.id, title);
    });
    return map;
  }, [units]);

  const topicTitleMap = useMemo(() => {
    const map = new Map<string, string>();
    units.forEach((unit) => {
      unit.topics.forEach((topic) => {
        const title = topic.title ?? (topic as { name?: string }).name ?? topic.slug;
        map.set(topic.slug, title);
      });
    });
    return map;
  }, [units]);

  const [showAnsweredOpen, setShowAnsweredOpen] = useState(false);

  const answeredList = useMemo(() => {
    const rows = answeredQuery.data ?? [];
    return rows.map((row) => ({
      id: row.id ?? crypto.randomUUID(),
      questionId: row.id,
      unitId: row.unitId,
      topicSlug: row.topicSlug ?? "",
      createdAt: row.createdAt ?? null,
    }));
  }, [answeredQuery.data]);

  const [unitIds, setUnitIds] = useState<string[]>([]);
  const [topicSlugs, setTopicSlugs] = useState<string[]>([]);
  const [difficulty, setDifficulty] = useState<typeof DIFFICULTIES[number]>("All");
  const [count, setCount] = useState<number>(20);

  // Detect any in-progress draft to allow resuming
  const draftKey = useMemo(
    () => (user?.id && slug ? `practice-draft:${user.id}:${slug}` : null),
    [user?.id, slug],
  );
  const [draftConfig, setDraftConfig] = useState<{
    unitIds: string[];
    topicSlugs: string[];
    difficulty: string;
    count: number;
  } | null>(null);

  useEffect(() => {
    if (!draftKey) {
      setDraftConfig(null);
      return;
    }
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) {
        setDraftConfig(null);
        return;
      }
      const parsed = JSON.parse(raw) as {
        sessionId?: string | null;
        questions?: unknown[] | null;
        config?: { unitIds?: string[]; topicSlugs?: string[]; difficulty?: string; count?: number };
      } | null;
      if (parsed?.sessionId && Array.isArray(parsed?.questions) && parsed!.questions!.length > 0) {
        const cfg = parsed?.config ?? {};
        setDraftConfig({
          unitIds: Array.isArray(cfg.unitIds) ? cfg.unitIds : [],
          topicSlugs: Array.isArray(cfg.topicSlugs) ? cfg.topicSlugs : [],
          difficulty: typeof cfg.difficulty === "string" ? cfg.difficulty : "all",
          count: typeof cfg.count === "number" ? cfg.count : 20,
        });
      } else {
        setDraftConfig(null);
      }
    } catch {
      setDraftConfig(null);
    }
  }, [draftKey]);

  const hasDraft = Boolean(draftConfig);

  const continueDraft = () => {
    if (!slug || !draftConfig) return;
    const params = new URLSearchParams();
    draftConfig.unitIds.forEach((id) => params.append("unitId", id));
    draftConfig.topicSlugs.forEach((t) => params.append("topicSlug", t));
    params.set("difficulty", draftConfig.difficulty.toLowerCase());
    params.set("count", String(draftConfig.count));
    navigate(`/practice/${slug}/test?${params.toString()}`);
  };

  useEffect(() => {
    const unitsParam = search.getAll("unitId");
    const topicsParam = search.getAll("topicSlug");
    const diffParam = search.get("difficulty");
    const countParam = search.get("count");
    if (unitsParam.length) setUnitIds(unitsParam);
    if (topicsParam.length) setTopicSlugs(topicsParam);
    if (diffParam && (DIFFICULTIES as readonly string[]).includes(diffParam)) setDifficulty(diffParam as typeof difficulty);
    if (countParam) setCount(parseInt(countParam, 10));
  }, [search]);

  useEffect(() => {
    if (!unitIds.length || unitAliasMap.size === 0) return;
    setUnitIds((prev) => {
      const resolved = Array.from(new Set(prev.map((value) => unitAliasMap.get(value) ?? value))).filter((value) =>
        unitAliasMap.has(value),
      );
      if (resolved.length !== prev.length || resolved.some((value, index) => value !== prev[index])) {
        return resolved;
      }
      return prev;
    });
  }, [unitAliasMap, unitIds.length]);

  const start = () => {
    if (!user) {
      toast({ title: "Sign in required", description: "Sign in to start a practice session." });
      return;
    }
    if (!subject) {
      toast({ title: "Subject unavailable", description: "Please try again once the subject loads." });
      return;
    }
    if (unitIds.length === 0 && topicSlugs.length === 0) {
      toast({ title: "No selection", description: "Select at least one unit or topic to continue." });
      return;
    }
    const params = new URLSearchParams();
    const unitSet = new Set<string>();
    unitIds.forEach((value) => {
      const resolved = unitAliasMap.get(value);
      if (resolved) unitSet.add(resolved);
    });
    topicSlugs.forEach((topicSlug) => {
      const unitId = topicToUnitId.get(topicSlug);
      if (unitId) unitSet.add(unitId);
    });

    const effectiveUnits = Array.from(unitSet);
    if (!effectiveUnits.length) {
      toast({ title: "No selection", description: "Select at least one unit or topic to continue." });
      return;
    }

    const uniqueTopics = Array.from(new Set(topicSlugs));

    effectiveUnits
      .sort()
      .forEach((id) => params.append("unitId", id));
    uniqueTopics.forEach((slug) => params.append("topicSlug", slug));
    params.set("difficulty", difficulty.toLowerCase());
    params.set("count", String(count));
    navigate(`/practice/${slug}/test?${params.toString()}`);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border">
            <CardHeader>
              <CardTitle className="text-xl">Practice sessions</CardTitle>
              <CardDescription>Sign in to build custom practice sets and track your progress.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild>
                <Link to="/auth">Sign in</Link>
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (outlineQuery.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border">
            <CardHeader>
              <CardTitle className="text-xl">Loading subject…</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-32 rounded-lg bg-muted/40 animate-pulse" />
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="rounded-md"
              onClick={() => {
                navigate("/dashboard");
              }}
            >
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <Badge variant="secondary" className="ml-1">Practice Setup</Badge>
            <Dialog open={showAnsweredOpen} onOpenChange={setShowAnsweredOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="ml-2" disabled={answeredQuery.isLoading}>
                  Answered ({answeredList.length})
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Answered questions</DialogTitle>
                  <DialogDescription>
                    Recently answered questions are excluded from new sessions. Clearing them will reintroduce them to future practice sets.
                  </DialogDescription>
                </DialogHeader>
                {answeredQuery.isLoading ? (
                  <div className="py-6 text-sm text-muted-foreground">Loading…</div>
                ) : answeredList.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-4">You have not answered any questions yet.</div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <div className="text-xs text-muted-foreground">Total: {answeredList.length}</div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => clearAnsweredMutation.mutate()}
                        disabled={clearAnsweredMutation.isPending}
                      >
                        {clearAnsweredMutation.isPending ? "Clearing…" : "Clear all"}
                      </Button>
                    </div>
                    <ScrollArea className="h-60 border rounded-md p-2 bg-muted/30">
                      <ul className="text-xs space-y-1 pr-2">
                        {answeredList.map((item) => {
                          const unitLabel = unitTitleMap.get(item.unitId) ?? item.unitId;
                          const topicLabel = topicTitleMap.get(item.topicSlug) ?? item.topicSlug;
                          return (
                            <li key={item.id} className="flex justify-between gap-2">
                              <span className="font-medium">{unitLabel}</span>
                              <span className="text-muted-foreground">{topicLabel}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </ScrollArea>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
          <div className="flex items-center gap-4">
            {hasDraft && (
              <Button size="sm" onClick={continueDraft} className="rounded-md">
                Continue last test
              </Button>
            )}
            <Link 
              to={`/subjects/${slug}`} 
              className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors"
            >
              View all practice options <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <Card className="border bg-gradient-card">
          <CardHeader className="pb-2 text-center">
            <CardTitle className="text-base">Select settings</CardTitle>
            <CardDescription>Choose units, topics, difficulty, and number of questions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setUnitIds(units.map((u) => u.id));
                        const allTopics = units.flatMap((u) => u.topics.map((t) => t.slug));
                        setTopicSlugs(allTopics);
                      }}
                    >
                      Select all
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setUnitIds([]);
                        setTopicSlugs([]);
                      }}
                    >
                      Clear
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {units.map((unit) => {
                    const unitTitle = (unit.name ?? (unit as { title?: string }).title ?? unit.id) as string;
                    return (
                      <div key={unit.id} className="rounded-lg border p-4 bg-card hover:shadow-sm transition">
                        <div className="flex items-start justify-between gap-2">
                          <span className="font-bold text-base leading-snug pr-4">{unitTitle}</span>
                        </div>
                        <div className="mt-3 ml-6">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {unit.topics.map((topic) => {
                              const topicTitle = topic.title ?? (topic as { name?: string }).name ?? topic.slug;
                              const checked = topicSlugs.includes(topic.slug);
                              return (
                                <label key={topic.slug} className="flex items-center gap-2 text-sm">
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={(value) => {
                                      const on = Boolean(value);
                                      setTopicSlugs((prev) => {
                                        const set = new Set(prev);
                                        if (on) set.add(topic.slug);
                                        else set.delete(topic.slug);
                                        return Array.from(set);
                                      });
                                    }}
                                  />
                                  <span>{topicTitle}</span>
                                </label>
                              );
                            })}
                          </div>
                          <div className="pt-3 flex gap-2 flex-wrap">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setUnitIds((prev) => (prev.includes(unit.id) ? prev : [...prev, unit.id]));
                                setTopicSlugs((prev) => {
                                  const set = new Set(prev);
                                  unit.topics.forEach((topic) => set.add(topic.slug));
                                  return Array.from(set);
                                });
                              }}
                            >
                              Select all topics
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setUnitIds((prev) => prev.filter((id) => id !== unit.id));
                                setTopicSlugs((prev) => {
                                  const set = new Set(prev);
                                  unit.topics.forEach((topic) => set.delete(topic.slug));
                                  return Array.from(set);
                                });
                              }}
                            >
                              Clear topics
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div>
                  <Label className="text-xs text-muted-foreground">Difficulty</Label>
                  <Select value={difficulty} onValueChange={(value) => setDifficulty(value as typeof difficulty)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Difficulty" />
                    </SelectTrigger>
                    <SelectContent>
                      {DIFFICULTIES.map((level) => (
                        <SelectItem key={level} value={level}>
                          {level}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground"># of questions</Label>
                  <Select value={String(count)} onValueChange={(value) => setCount(parseInt(value, 10))}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Count" />
                    </SelectTrigger>
                    <SelectContent>
                      {[10, 15, 20, 25, 35, 50].map((qty) => (
                        <SelectItem key={qty} value={String(qty)}>
                          {qty}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:justify-self-end">
                  <Button className="w-full md:w-auto" onClick={start}>
                    Start practice
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
